import { useState } from 'react';
import UserAvatar from '../UserAvatar';
import MobileSheet from './MobileSheet';
import CustomSelect from '../CustomSelect';
import ConfirmModal from '../ConfirmModal';
import MobileItemDetailSheet from './MobileItemDetailSheet';
import MobileItemDetailView from './MobileItemDetailView';
import { MobileAddFormFields, MobileEditFormFields } from './MobileItemFormFields';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, HistoryIcon, InboxIcon, CopyIcon, DeleteIcon } from './Icons';
import { TYPE_CONFIG, STATUS_LABELS, LOCATION_OPTIONS, PRIORITY_CONFIG } from '../../constants';
import { formatDate, findAssignee, buildUserOptions, buildItemTree, getAllowedChildTypes, isContainerType } from '../../utils';
import useBacklogHistory from '../../hooks/useBacklogHistory';
import useMobileItemForm from '../../hooks/useMobileItemForm';
import './mobile.css';

function MobileTreeItem({ node, depth, expandedIds, onToggle, onClickItem, onAddChild, onLocationChange, onDeleteItem, users, showToast }) {
  const config = TYPE_CONFIG[node.type] || TYPE_CONFIG.task;
  const Icon = config.icon;
  const assignee = findAssignee(users, node.assigneeId);
  const isContainer = isContainerType(node.type);
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedIds[node.id];
  const childTypes = getAllowedChildTypes(node.type);
  const statusLabel = STATUS_LABELS[node.status] || 'Not Started';

  return (
    <>
      <div
        className={`mobile-backlog-item${isContainer ? ' mobile-backlog-item--container' : ''}`}
        style={{ paddingLeft: `${depth * 20 + 12}px` }}
        onClick={() => onClickItem(node)}
      >
        <div className="mobile-sprint-card-left">
          {isContainer && (
            <span
              className={`backlog-history-chevron${isExpanded ? ' backlog-history-chevron--open' : ''}`}
              onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <ChevronRightIcon size={14} />
            </span>
          )}
          <span style={{ color: config.color }}><Icon size={18} /></span>
          <div>
            {node.readableId && (
              <span
                className="backlog-item-rid"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(`${node.readableId}-${node.title}`);
                  showToast('ID copied', 'success');
                }}
              >
                {node.readableId} <CopyIcon size={10} />
              </span>
            )}
            <div className="mobile-sprint-card-title">{node.title}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          {isContainer && hasChildren && (
            <span className="backlog-tree-child-count">{node.children.length}</span>
          )}
          {assignee && <UserAvatar user={assignee} size={28} showStatus={false} />}
          {node.storyPoints != null && <span className="sprint-item-points">{node.storyPoints}</span>}
          <span className={`status-badge status-${node.status}`} style={{ fontSize: '0.6875rem', padding: '2px 8px' }}>
            {statusLabel}
          </span>
          {onLocationChange && (
            <div onClick={(e) => e.stopPropagation()}>
              <CustomSelect
                value={node.location || 'backlog'}
                onChange={(val) => onLocationChange(node.id, val)}
                options={LOCATION_OPTIONS}
                size="sm"
                compact
              />
            </div>
          )}
          {childTypes.length > 0 && (
            <button
              className="backlog-tree-add-child"
              onClick={(e) => { e.stopPropagation(); onAddChild(node); }}
            >
              <PlusIcon size={14} />
            </button>
          )}
          {onDeleteItem && (
            <button
              className="backlog-tree-delete"
              onClick={(e) => { e.stopPropagation(); onDeleteItem(node); }}
            >
              <DeleteIcon size={14} />
            </button>
          )}
        </div>
      </div>
      {isExpanded && hasChildren && node.children.map((child) => (
        <MobileTreeItem
          key={child.id}
          node={child}
          depth={depth + 1}
          expandedIds={expandedIds}
          onToggle={onToggle}
          onClickItem={onClickItem}
          onAddChild={onAddChild}
          onLocationChange={onLocationChange}
          onDeleteItem={onDeleteItem}
          users={users}
          showToast={showToast}
        />
      ))}
    </>
  );
}

export default function BacklogMobile({ items, onBack, onUpdateItem, onDeleteItem, onAddItem, showToast, users, userProfile, projectId, isKanban }) {
  const [showHistory, setShowHistory] = useState(false);
  const [expandedIds, setExpandedIds] = useState({});
  const [addParentContext, setAddParentContext] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { historySprints, loadingHistory, expandedSprints, toggleSprint } = useBacklogHistory({ showToast, projectId, showHistory });

  const {
    selectedItem, setSelectedItem,
    showDetail, editing, showAdd, setShowAdd, showConfirm, setShowConfirm,
    addForm, editForm,
    openDetail, openDirectEdit, startEditing, resetAddForm, closeDetail, buildAddData, buildEditData,
  } = useMobileItemForm({ isKanban });

  const totalPoints = items.reduce((sum, i) => sum + (i.storyPoints || 0), 0);
  const userOptions = buildUserOptions(users);

  const handleToggle = (id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleLocationChange = async (itemId, newLocation) => {
    try {
      await onUpdateItem(itemId, { location: newLocation });
    } catch (err) {
      showToast(err.message || 'Failed to move item', 'error');
    }
  };

  const handleTreeDelete = (node) => {
    const childCount = isContainerType(node.type)
      ? items.filter((i) => i.parentId === node.id).length
      : 0;
    setDeleteTarget({ ...node, childCount });
  };

  const confirmTreeDelete = async () => {
    if (!deleteTarget) return;
    try {
      await onDeleteItem(deleteTarget.id);
      setDeleteTarget(null);
      showToast('Item deleted', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to delete item', 'error');
    }
  };

  const handleAddChild = (parentNode) => {
    setAddParentContext(parentNode);
    addForm.setParentId(parentNode.id);
    const childTypes = getAllowedChildTypes(parentNode.type);
    if (childTypes.length > 0) addForm.setType(childTypes[0]);
    setShowAdd(true);
  };

  const handleOpenAdd = () => {
    setAddParentContext(null);
    addForm.setParentId(null);
    setShowAdd(true);
  };

  const handleAdd = async () => {
    if (!addForm.title.trim()) return;
    try {
      await onAddItem(buildAddData('backlog'));
      resetAddForm();
      setAddParentContext(null);
    } catch (err) {
      showToast(err.message || 'Failed to add item', 'error');
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.title.trim()) {
      showToast('Title is required', 'error');
      return;
    }
    try {
      await onUpdateItem(selectedItem.id, buildEditData({ location: editForm.location }));
      closeDetail();
    } catch (err) {
      showToast(err.message || 'Failed to update item', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await onDeleteItem(selectedItem.id);
      setShowConfirm(false);
      closeDetail();
    } catch (err) {
      showToast(err.message || 'Failed to delete item', 'error');
    }
  };

  if (showHistory) {
    return (
      <div className="mobile-page mobile-backlog-page">
        <div className="mobile-backlog-header">
          <button className="mobile-btn-icon" onClick={() => setShowHistory(false)}>
            <ChevronLeftIcon size={20} />
          </button>
          <h2>Backlog History</h2>
        </div>
        <div className="mobile-backlog-body">
          {loadingHistory ? (
            <div className="mobile-loading" style={{ flex: 1 }}>
              <div className="mobile-loading-spinner" />
              <p>Loading history...</p>
            </div>
          ) : historySprints.length === 0 ? (
            <div className="mobile-backlog-empty">
              No completed items found in closed sprints.
            </div>
          ) : (
            historySprints.map(({ sprint: s, items: sItems }) => {
              const isExpanded = expandedSprints[s.id];
              const doneCount = sItems.filter((i) => i.status === 'done').length;
              return (
                <div key={s.id} className="mobile-backlog-history-section">
                  <div className="mobile-backlog-history-header" onClick={() => toggleSprint(s.id)}>
                    <ChevronRightIcon size={16} className={`backlog-history-chevron${isExpanded ? ' backlog-history-chevron--open' : ''}`} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: 'var(--white)', fontWeight: 600, fontSize: '0.9375rem' }}>{s.name}</div>
                      <div style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}>
                        {formatDate(s.startDate)} &ndash; {formatDate(s.endDate)} &middot; {doneCount} completed
                      </div>
                    </div>
                  </div>
                  {isExpanded && sItems.map((item) => {
                    const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.task;
                    const Icon = config.icon;
                    const assignee = findAssignee(users, item.assigneeId);
                    const statusLabel = STATUS_LABELS[item.status] || 'Not Started';
                    return (
                      <div key={item.id} className="mobile-backlog-item" style={{ cursor: 'default', paddingLeft: '32px' }}>
                        <div className="mobile-sprint-card-left">
                          <span style={{ color: config.color }}><Icon size={18} /></span>
                          <div>
                            {item.readableId && <span className="sprint-item-rid">{item.readableId}</span>}
                            <div className="mobile-sprint-card-title">{item.title}</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                          {assignee && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <UserAvatar user={assignee} size={26} showStatus={false} />
                              <span style={{ color: 'var(--gray-400)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{assignee.displayName}</span>
                            </div>
                          )}
                          {item.storyPoints != null && <span className="sprint-item-points">{item.storyPoints}</span>}
                          <span className={`status-badge status-${item.status}`} style={{ fontSize: '0.6875rem', padding: '2px 8px' }}>
                            {statusLabel}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  // Scrum mode: render ALL items in hierarchy tree (items on board stay in their parent position)
  const tree = !isKanban ? buildItemTree(items) : [];

  return (
    <div className="mobile-page mobile-backlog-page">
      <div className="mobile-backlog-header">
        <button className="mobile-btn-icon" onClick={onBack}>
          <ChevronLeftIcon size={20} />
        </button>
        <h2><InboxIcon size={18} /> Backlog</h2>
        <button className="mobile-btn-icon" onClick={() => setShowHistory(true)} title="History">
          <HistoryIcon size={18} />
        </button>
      </div>

      <div className="mobile-backlog-toolbar">
        <span>{items.length} item{items.length !== 1 ? 's' : ''} &middot; {totalPoints} pts</span>
      </div>

      <div className="mobile-backlog-body">
        {items.length === 0 ? (
          <div className="mobile-backlog-empty">
            No items yet.
          </div>
        ) : !isKanban ? (
          tree.map((node) => (
            <MobileTreeItem
              key={node.id}
              node={node}
              depth={0}
              expandedIds={expandedIds}
              onToggle={handleToggle}
              onClickItem={openDirectEdit}
              onAddChild={handleAddChild}
              onLocationChange={handleLocationChange}
              onDeleteItem={handleTreeDelete}
              users={users}
              showToast={showToast}
            />
          ))
        ) : (
          (isKanban ? [...items].sort((a, b) => (PRIORITY_CONFIG[a.priority]?.order ?? 1) - (PRIORITY_CONFIG[b.priority]?.order ?? 1)) : items).map((item) => {
            const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.task;
            const Icon = config.icon;
            const assignee = findAssignee(users, item.assigneeId);
            const isOnBoard = !item.location || item.location === 'board';
            const statusLabel = STATUS_LABELS[item.status] || 'Not Started';
            const priorityColor = isKanban && item.priority ? PRIORITY_CONFIG[item.priority]?.color : null;
            return (
              <div
                key={item.id}
                className={`mobile-backlog-item${isOnBoard ? ' mobile-backlog-item--on-board' : ''}${priorityColor ? ' mobile-backlog-item--priority' : ''}`}
                style={priorityColor ? {
                  background: `${priorityColor}18`,
                  borderColor: `${priorityColor}40`,
                  borderLeftColor: priorityColor,
                  borderLeftWidth: '3px',
                } : undefined}
                onClick={() => openDetail(item)}
              >
                <div className="mobile-sprint-card-left">
                  <span style={{ color: config.color }}><Icon size={18} /></span>
                  <div>
                    <div className="backlog-item-info-top">
                      {item.readableId && (
                        <span
                          className="backlog-item-rid"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(`${item.readableId}-${item.title}`);
                            showToast('ID copied', 'success');
                          }}
                        >
                          {item.readableId} <CopyIcon size={10} />
                        </span>
                      )}
                      {isKanban && item.priority && (
                        <span className="backlog-priority-dot" style={{ background: priorityColor }}>{PRIORITY_CONFIG[item.priority]?.label}</span>
                      )}
                    </div>
                    <div className="mobile-sprint-card-title">{item.title}</div>
                    {item.description && <div className="mobile-sprint-card-desc">{item.description}</div>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                  {assignee && <UserAvatar user={assignee} size={28} showStatus={false} />}
                  {item.storyPoints != null && <span className="sprint-item-points">{item.storyPoints}</span>}
                  <span className={`status-badge status-${item.status}`} style={{ fontSize: '0.6875rem', padding: '2px 8px' }}>
                    {statusLabel}
                  </span>
                  {isOnBoard && (
                    <span className="backlog-on-board-badge">{`On Board`}</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <button className="mobile-fab" onClick={handleOpenAdd}>
        <PlusIcon size={24} />
      </button>

      {showAdd && (
        <MobileSheet
          title={addParentContext ? `Add child to ${addParentContext.title}` : 'Add Backlog Item'}
          onClose={() => { setShowAdd(false); setAddParentContext(null); }}
        >
          <div className="mobile-form">
            <MobileAddFormFields
              form={addForm}
              userOptions={userOptions}
              isKanban={isKanban}
              allowedTypes={addParentContext ? getAllowedChildTypes(addParentContext.type) : null}
            />
            <button className="mobile-btn mobile-btn-primary" onClick={handleAdd} disabled={!addForm.title.trim()}>
              Add to Backlog
            </button>
          </div>
        </MobileSheet>
      )}

      {showDetail && selectedItem && (
        <MobileItemDetailSheet
          editing={editing}
          onClose={closeDetail}
          onDelete={() => setShowConfirm(true)}
          onSave={handleSaveEdit}
          onEdit={startEditing}
        >
          {editing ? (
            <MobileEditFormFields form={editForm} userOptions={userOptions} isKanban={isKanban}>
              <div className="mobile-input-group">
                <label className="mobile-label">Location</label>
                <CustomSelect value={editForm.location} onChange={editForm.setLocation} options={LOCATION_OPTIONS} />
              </div>
            </MobileEditFormFields>
          ) : (
            <MobileItemDetailView
              item={selectedItem}
              users={users}
              isKanban={isKanban}
              onStatusChange={(id, val) => {
                onUpdateItem(id, { status: val });
                setSelectedItem({ ...selectedItem, status: val });
              }}
              onAssigneeChange={(id, val) => {
                onUpdateItem(id, { assigneeId: val || null });
                setSelectedItem({ ...selectedItem, assigneeId: val || null });
              }}
            />
          )}
        </MobileItemDetailSheet>
      )}

      {showConfirm && (
        <ConfirmModal
          message="Are you sure you want to delete this item? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          message={`Delete "${deleteTarget.title}"?${deleteTarget.childCount > 0 ? ` This will also delete ${deleteTarget.childCount} child item${deleteTarget.childCount > 1 ? 's' : ''}.` : ''} This action cannot be undone.`}
          onConfirm={confirmTreeDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
